import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeatureSettings } from '@/hooks/use-feature-settings';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CheckSquare, Plus, LayoutList, LayoutGrid, Columns3, ChevronDown, Search, ArrowUpDown, FolderTree, UserPlus, Users, AlertCircle, Download, Trash2, ListChecks, ArrowRight, Zap, Filter, Columns } from 'lucide-react';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { AuditsListView } from '@/components/audits/AuditsListView';
import { AuditsCardView } from '@/components/audits/AuditsCardView';
import { AuditsKanbanView } from '@/components/audits/AuditsKanbanView';
import { ActiveAuditsFiltersTab } from '@/components/RightPanel/ActiveAuditsFiltersTab';
import { auditDataService } from '@/services/audit-data-service';
import type { Audit, AuditStatus as AuditStatusType, AuditPriority } from '@/types/asset-audit';
import { AuditStatus, AssigneeStatus } from '@/types/asset-audit';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ActiveAuditsColumnsTab } from '@/components/RightPanel/ActiveAuditsColumnsTab';
import { DEFAULT_ACTIVE_AUDITS_COLUMNS } from '@/lib/active-audits-columns-config';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useTeams } from '@/hooks/use-teams';
import { useUsers } from '@/hooks/use-users';
import { getIconByName } from '@/lib/team-icons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { ProductTour } from '@/components/ui/product-tour';
import { useTour } from '@/contexts/TourContext';
import { useActiveAuditsTour } from '@/hooks/use-active-audits-tour';
import { useAuditPermissions } from '@/hooks/use-permissions';

type ViewMode = 'list' | 'card' | 'kanban';
type GroupBy = 'none' | 'dueDate' | 'type' | 'priority' | 'status';
type SortOption = 'dueDate-asc' | 'dueDate-desc' | 'createdDate-asc' | 'createdDate-desc' | 'priority-asc' | 'priority-desc' | 'name-asc' | 'name-desc';

function getSortByField(sortField: string): string {
  if (sortField === 'dueDate') return 'scheduledEndDate';
  if (sortField === 'name') return 'title';
  return sortField;
}

// Helper function to check if audit matches quick filter - extracted to reduce cognitive complexity (S3776)
function matchesQuickFilter(audit: Audit, quickFilter: string | null): boolean {
  if (!quickFilter) return true;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  switch (quickFilter) {
    case 'overdue': {
      const isOverdue = audit.scheduledEndDate && new Date(audit.scheduledEndDate) < new Date();
      return !!isOverdue;
    }
    case 'dueToday': {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const auditDue = audit.scheduledEndDate ? new Date(audit.scheduledEndDate) : null;
      return !!(auditDue && auditDue >= today && auditDue < tomorrow);
    }
    case 'dueThisWeek': {
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);
      const auditDue = audit.scheduledEndDate ? new Date(audit.scheduledEndDate) : null;
      return !!(auditDue && auditDue >= today && auditDue < nextWeek);
    }
    case 'inProgress':
      return audit.status === AuditStatus.InProgress;
    case 'highPriority':
      return audit.priority === 'High' || audit.priority === 'Critical';
    default:
      return true;
  }
}

export default function ActiveAudits() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { modules } = useFeatureSettings();
  const auditsModule = modules?.audits;
  const { toast } = useToast();
  const { teams, loading: teamsLoading } = useTeams();
  const { users, loading: usersLoading } = useUsers();
  const { canCreateAudits, canEditAudits } = useAuditPermissions();
  
  // Product Tour
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useActiveAuditsTour();
  const { setOnStartTour } = useTour();
  
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('activeAuditsViewMode');
    return (saved as ViewMode) ?? 'list';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('dueDate-asc');
  const [groupBy, setGroupBy] = useState<GroupBy>('dueDate');

  // Dialog states
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<AuditStatusType | ''>('');
  const [assignUsersDialogOpen, setAssignUsersDialogOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState<'teams' | 'users'>('teams');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set());
  const [changePriorityDialogOpen, setChangePriorityDialogOpen] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  // Right Panel Context
  const {
    setIsExpanded,
    setActiveTab,
    selectedRowIds,
    setSelectedRowIds,
    tableFilters,
    setTableFilters,
    setPendingFilters,
    isPinned,
    visibleColumns: contextVisibleColumns,
    setVisibleColumns,
    isApplyingFilters
  } = useRightPanel();

  // Quick filters - single selection only (radio-style)
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);

const quickFilters = [
    { id: 'overdue', name: 'Overdue', icon: '🔴', color: 'text-red-600' },
    { id: 'dueToday', name: 'Due Today', icon: '📅' },
    { id: 'dueThisWeek', name: 'Due This Week', icon: '📆' },
    { id: 'inProgress', name: 'In Progress', icon: '🔄' },
    { id: 'highPriority', name: 'High Priority', icon: '⚡', color: 'text-orange-600' },
  ];

  // Initialize filters and columns on mount - clear any filters from other pages
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (!isInitialized) {
      const filterParam = searchParams.get('filter');
      const statusParam = searchParams.get('status');
      const typeParam = searchParams.get('type');
      const priorityParam = searchParams.get('priority');
      
      // ALWAYS clear filters first to prevent leaking from other pages
      // This ensures a clean slate before applying any deep-link filters
      setTableFilters({});
      
      // Check if there are URL parameters for deep linking
      if (statusParam || typeParam || priorityParam) {
        // Deep link: set filters from URL params
        const filters: any = {};
        if (statusParam) filters.status = statusParam.split(',');
        if (typeParam) filters.type = typeParam.split(',');
        if (priorityParam) filters.priority = priorityParam.split(',');
        
        setTableFilters(filters);
        setPendingFilters({
          selectedAuditStatuses: statusParam ? statusParam.split(',') : [],
          selectedAuditTypes: typeParam ? typeParam.split(',') : [],
          selectedAuditPriorities: priorityParam ? priorityParam.split(',') : [],
          selectedCategories: [],
          selectedStatuses: [],
          selectedLocations: [],
          selectedSubCategories: [],
          selectedSubLocations: [],
          acquisitionDateRange: { from: undefined, to: undefined },
          acquisitionCostMin: '',
          acquisitionCostMax: '',
          netBookValueMin: '',
          netBookValueMax: '',
          selectedAssignedTo: [],
          selectedDepreciationMethods: [],
          warrantyExpiryDateRange: { from: undefined, to: undefined },
          selectedManufacturers: [],
          auditScheduledDateRange: { from: undefined, to: undefined },
          completionDateRange: { from: undefined, to: undefined },
          completionRateMin: undefined,
          completionRateMax: undefined,
          totalAssetsMin: undefined,
          totalAssetsMax: undefined,
          selectedCreatedBy: [],
        });
      } else {
        // No deep link: Initialize pending filters with empty values
        setPendingFilters({
          selectedAuditStatuses: [],
          selectedAuditTypes: [],
          selectedAuditPriorities: [],
          selectedCategories: [],
          selectedStatuses: [],
          selectedLocations: [],
          selectedSubCategories: [],
          selectedSubLocations: [],
          acquisitionDateRange: { from: undefined, to: undefined },
          acquisitionCostMin: '',
          acquisitionCostMax: '',
          netBookValueMin: '',
          netBookValueMax: '',
          selectedAssignedTo: [],
          selectedDepreciationMethods: [],
          warrantyExpiryDateRange: { from: undefined, to: undefined },
          selectedManufacturers: [],
          auditScheduledDateRange: { from: undefined, to: undefined },
          completionDateRange: { from: undefined, to: undefined },
          completionRateMin: undefined,
          completionRateMax: undefined,
          totalAssetsMin: undefined,
          totalAssetsMax: undefined,
          selectedCreatedBy: [],
        });
      }
      
      // Initialize visible columns if not already set
      if (!contextVisibleColumns || contextVisibleColumns.length === 0) {
        const defaultVisible = DEFAULT_ACTIVE_AUDITS_COLUMNS.filter(col => col.visible).map(col => col.id);
        setVisibleColumns(defaultVisible);
      }
      
      if (!filterParam) {
        // No deep-link params, reset all local filters
        setActiveQuickFilter(null);
        setSearchQuery('');
      }
      
      setIsInitialized(true);
    }
  }, [isInitialized, searchParams, setTableFilters, setPendingFilters, contextVisibleColumns, setVisibleColumns]); // Only run on mount

  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => {
      setOnStartTour(null);
    };
  }, [setOnStartTour, startTour]);

  // Load audits - only after initialization is complete
  useEffect(() => {
    // Don't load audits until initialization is complete
    if (!isInitialized) return;
    
    async function loadAudits() {
      try {
        setLoading(true);
        
        // Parse sortBy into field and order
        const [sortField, sortOrder] = sortBy.split('-') as [string, 'asc' | 'desc'];
        
        // Default to showing all active statuses
        const defaultStatuses = [
          AuditStatus.Draft,
          AuditStatus.Assigned,
          AuditStatus.InProgress,
          AuditStatus.UnderReview,
          AuditStatus.OnHold,
          AuditStatus.Scheduled
        ];
        
        console.log('Loading audits with tableFilters:', tableFilters);
        
        // Only pass priorities and types if they exist and have values
        const priorities = tableFilters.priority && Array.isArray(tableFilters.priority) && tableFilters.priority.length > 0 
          ? tableFilters.priority 
          : undefined;
        const types = tableFilters.type && Array.isArray(tableFilters.type) && tableFilters.type.length > 0 
          ? tableFilters.type 
          : undefined;
        
        console.log('Calling getAudits with:', {
          statuses: (tableFilters.status as AuditStatusType[]) || defaultStatuses,
          priorities,
          types,
        });
        
        const result = await auditDataService.getAudits({
          statuses: (tableFilters.status as AuditStatusType[]) || defaultStatuses,
          sortBy: getSortByField(sortField),
          sortOrder: sortOrder,
          page: 1,
          pageSize: 100,
          priorities,
          types,
        });
        
        console.log('Loaded audits:', result.items?.length, 'items');
        setAudits(result.items ?? []);
      } catch (err) {
        console.error('Failed to load audits:', err);
      } finally {
        setLoading(false);
      }
    }
    loadAudits();
  }, [sortBy, tableFilters, isInitialized]);

  // Check for filter param from dashboard
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam === 'overdue') {
      setActiveQuickFilter('overdue');
    }
  }, [searchParams]);

  // Filter audits based on search, quick filters, and table filters
  const filteredAudits = audits.filter((audit) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        audit.name?.toLowerCase().includes(query) ||
        audit.title?.toLowerCase().includes(query) ||
        audit.auditNumber?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Quick filters - use extracted helper function to reduce cognitive complexity
    if (!matchesQuickFilter(audit, activeQuickFilter)) return false;

    // Table Filters from Right Panel
    
    // Status filter
    if (tableFilters.status && (tableFilters.status as string[]).length > 0) {
      if (!(tableFilters.status as string[]).includes(audit.status)) return false;
    }

    // Type filter
    if (tableFilters.type && (tableFilters.type as string[]).length > 0) {
      if (!(tableFilters.type as string[]).includes(audit.type)) return false;
    }

    // Priority filter
    if (tableFilters.priority && (tableFilters.priority as string[]).length > 0) {
      if (!(tableFilters.priority as string[]).includes(audit.priority)) return false;
    }

    // Date range filter (Created Date)
    if (tableFilters.dateRange) {
      const auditDate = new Date(audit.createdDate);
      if (tableFilters.dateRange.from) {
        const fromDate = new Date(tableFilters.dateRange.from);
        if (auditDate < fromDate) return false;
      }
      if (tableFilters.dateRange.to) {
        const toDate = new Date(tableFilters.dateRange.to);
        toDate.setHours(23, 59, 59, 999);
        if (auditDate > toDate) return false;
      }
    }

    // Due date range filter
    if (tableFilters.dueDateRange && audit.scheduledEndDate) {
      const dueDate = new Date(audit.scheduledEndDate);
      if (tableFilters.dueDateRange.from) {
        const fromDate = new Date(tableFilters.dueDateRange.from);
        if (dueDate < fromDate) return false;
      }
      if (tableFilters.dueDateRange.to) {
        const toDate = new Date(tableFilters.dueDateRange.to);
        toDate.setHours(23, 59, 59, 999);
        if (dueDate > toDate) return false;
      }
    }

    // Completion rate filter
    if (tableFilters.completionRateMin !== undefined) {
      const minRate = parseFloat(tableFilters.completionRateMin as string);
      if ((audit.completionPercentage || 0) < minRate) return false;
    }
    if (tableFilters.completionRateMax !== undefined) {
      const maxRate = parseFloat(tableFilters.completionRateMax as string);
      if ((audit.completionPercentage || 0) > maxRate) return false;
    }

    // Total assets filter
    if (tableFilters.totalAssetsMin !== undefined) {
      const minAssets = parseInt(tableFilters.totalAssetsMin as string);
      if ((audit.totalAssets || 0) < minAssets) return false;
    }
    if (tableFilters.totalAssetsMax !== undefined) {
      const maxAssets = parseInt(tableFilters.totalAssetsMax as string);
      if ((audit.totalAssets || 0) > maxAssets) return false;
    }

    // Created by filter
    if (tableFilters.createdBy && (tableFilters.createdBy as string[]).length > 0) {
      if (!(tableFilters.createdBy as string[]).includes(audit.createdBy)) return false;
    }

    // Auditor filter (if audit has auditor field)
    if (tableFilters.auditor && (tableFilters.auditor as string[]).length > 0) {
      // Note: Audit type doesn't have auditor field, this is a placeholder
      // If auditor field is added to Audit type, uncomment below:
      // if (!(tableFilters.auditor as string[]).includes(audit.auditor)) return false;
    }

    return true;
  });

  // Row selection handler
  const handleSelectionChange = useCallback((rowId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(rowId);
        if (newSet.size === 1 && !isPinned) {
          setIsExpanded(true);
          setActiveTab('actions');
        }
      } else {
        newSet.delete(rowId);
        if (newSet.size === 0 && !isPinned) {
          setIsExpanded(false);
        }
      }
      return newSet;
    });
  }, [setIsExpanded, setActiveTab, setSelectedRowIds, isPinned]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('activeAuditsViewMode', mode);
  };

  const handleAuditClick = (auditId: string) => {
    navigate(`/audits/${auditId}`);
  };

  const handleEditAudit = (auditId: string) => {
    // Navigate to audit detail page in edit mode
    navigate(`/audits/${auditId}?edit=true`);
  };

  const handleDuplicateAudit = async (auditId: string) => {
    try {
      const auditToDuplicate = audits.find(a => a.id === auditId);
      if (!auditToDuplicate) {
        toast({
          title: 'Error',
          description: 'Audit not found',
          variant: 'destructive',
        });
        return;
      }

      // Create a duplicate audit with modified properties
      const duplicatedAudit = {
        ...auditToDuplicate,
        id: `audit-dup-${Date.now()}`,
        name: `${auditToDuplicate.name} (Copy)`,
        title: `${auditToDuplicate.title ?? auditToDuplicate.name} (Copy)`,
        status: AuditStatus.Draft,
        createdDate: new Date().toISOString(),
        completionPercentage: 0,
        assetsAudited: 0,
      };

      // In demo mode, add to local state
      setAudits(prev => [duplicatedAudit, ...prev]);

      toast({
        title: 'Audit Duplicated',
        description: `Created "${duplicatedAudit.name}" as a copy.`,
      });
    } catch (error) {
      console.error('Failed to duplicate audit:', error);
      toast({
        title: 'Error',
        description: 'Failed to duplicate audit',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteAudit = async (auditId: string) => {
    try {
      // Remove from local state (demo mode)
      setAudits(prev => prev.filter(a => a.id !== auditId));

      toast({
        title: 'Audit Deleted',
        description: 'The audit has been removed.',
      });
    } catch (error) {
      console.error('Failed to delete audit:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete audit',
        variant: 'destructive',
      });
    }
  };

  // Helper functions for user/team management
  const getTeamMembers = (teamId: string) => {
    return users.filter(u => u.teams?.some(t => typeof t === 'string' ? t === teamId : t.teamId === teamId));
  };

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const addTeamMembersToSelection = (teamId: string) => {
    const teamMembers = getTeamMembers(teamId);
    const memberIds = teamMembers.map((member: { id: string }) => member.id);
    setSelectedUserIds(prevUsers => new Set([...prevUsers, ...memberIds]));
  };

  const removeTeamMembersFromSelection = (teamId: string) => {
    const teamMembers = getTeamMembers(teamId);
    const memberIds = new Set(teamMembers.map((member: { id: string }) => member.id));
    setSelectedUserIds(prevUsers => new Set([...prevUsers].filter(id => !memberIds.has(id))));
  };

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teamId)) {
        newSet.delete(teamId);
        removeTeamMembersFromSelection(teamId);
      } else {
        newSet.add(teamId);
        addTeamMembersToSelection(teamId);
      }
      return newSet;
    });
  };

  const selectAllUsers = () => {
    setSelectedUserIds(new Set(users.map(u => u.id)));
  };

  const clearAllUsers = () => {
    setSelectedUserIds(new Set());
    setSelectedTeamIds(new Set());
  };

  const handleModeChange = (mode: 'teams' | 'users') => {
    setSelectionMode(mode);
    // Clear selections when switching modes
    if (mode === 'teams') {
      setSelectedUserIds(new Set());
    }
    setSelectedTeamIds(new Set());
  };

  // Bulk action handlers
  const handleChangeStatus = () => {
    setSelectedStatus('');
    setChangeStatusDialogOpen(true);
  };

  const handleChangeStatusSubmit = async () => {
    if (!selectedStatus) {
      toast({
        title: 'No Status Selected',
        description: 'Please select a status to apply.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const selectedAudits = Array.from(selectedRowIds);
      
      // Update each audit
      await Promise.all(
        selectedAudits.map(auditId =>
          auditDataService.updateAudit(auditId, { status: selectedStatus })
        )
      );

      toast({
        title: 'Status Updated',
        description: `Successfully updated ${selectedAudits.length} audit${selectedAudits.length !== 1 ? 's' : ''} to ${selectedStatus}.`,
      });

      setChangeStatusDialogOpen(false);
      setSelectedStatus('');
      
      // Reload audits
      const [sortField, sortOrder] = sortBy.split('-') as [string, 'asc' | 'desc'];
      const defaultStatuses = [
        AuditStatus.Draft,
        AuditStatus.Assigned,
        AuditStatus.InProgress,
        AuditStatus.UnderReview,
        AuditStatus.OnHold,
        AuditStatus.Scheduled
      ];
      
      const result = await auditDataService.getAudits({
        statuses: (tableFilters.status as AuditStatusType[]) || defaultStatuses,
        sortBy: getSortByField(sortField),
        sortOrder: sortOrder,
        page: 1,
        pageSize: 100,
      });
      
      setAudits(result.items || []);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update audit status. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleAssignUsers = () => {
    setSelectedUserIds(new Set());
    setSelectedTeamIds(new Set());
    setSelectionMode('teams');
    setAssignUsersDialogOpen(true);
  };

  const handleAssignUsersSubmit = async () => {
    if (selectedUserIds.size === 0) {
      toast({
        title: 'No Users Selected',
        description: 'Please select at least one user or team.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const selectedAudits = Array.from(selectedRowIds);
      const userIds = Array.from(selectedUserIds);
      
      // Get full user objects and convert to AuditAssignee format
      const assignedUsers = users.filter(u => userIds.includes(u.id)).map(u => ({
        userId: u.id,
        userName: u.name,
        userEmail: u.email,
        userAvatar: u.avatarUrl ?? u.avatar,
        assignedDate: new Date().toISOString(),
        status: AssigneeStatus.Pending,
        completedAssets: 0,
        notificationSent: false,
      }));

      // Update each audit
      await Promise.all(
        selectedAudits.map(auditId =>
          auditDataService.updateAudit(auditId, { assignedUsers })
        )
      );

      toast({
        title: 'Users Assigned',
        description: `Successfully assigned ${assignedUsers.length} user${assignedUsers.length !== 1 ? 's' : ''} to ${selectedAudits.length} audit${selectedAudits.length !== 1 ? 's' : ''}.`,
      });

      setAssignUsersDialogOpen(false);
      setSelectedUserIds(new Set());
      setSelectedTeamIds(new Set());
      
      // Reload audits
      const [sortField, sortOrder] = sortBy.split('-') as [string, 'asc' | 'desc'];
      const defaultStatuses = [
        AuditStatus.Draft,
        AuditStatus.Assigned,
        AuditStatus.InProgress,
        AuditStatus.UnderReview,
        AuditStatus.OnHold,
        AuditStatus.Scheduled
      ];
      
      const result = await auditDataService.getAudits({
        statuses: (tableFilters.status as AuditStatusType[]) || defaultStatuses,
        sortBy: getSortByField(sortField),
        sortOrder: sortOrder,
        page: 1,
        pageSize: 100,
      });
      
      setAudits(result.items || []);
    } catch (error) {
      console.error('Failed to assign users:', error);
      toast({
        title: 'Assignment Failed',
        description: 'Failed to assign users. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleChangePriority = () => {
    setSelectedPriority('');
    setChangePriorityDialogOpen(true);
  };

  const handleChangePrioritySubmit = async () => {
    if (!selectedPriority) {
      toast({
        title: 'No Priority Selected',
        description: 'Please select a priority to apply.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const selectedAudits = Array.from(selectedRowIds);
      
      // Update each audit
      await Promise.all(
        selectedAudits.map(auditId =>
          auditDataService.updateAudit(auditId, { priority: selectedPriority as AuditPriority })
        )
      );

      toast({
        title: 'Priority Updated',
        description: `Successfully updated ${selectedAudits.length} audit${selectedAudits.length !== 1 ? 's' : ''} to ${selectedPriority} priority.`,
      });

      setChangePriorityDialogOpen(false);
      setSelectedPriority('');
      
      // Reload audits
      const [sortField, sortOrder] = sortBy.split('-') as [string, 'asc' | 'desc'];
      const defaultStatuses = [
        AuditStatus.Draft,
        AuditStatus.Assigned,
        AuditStatus.InProgress,
        AuditStatus.UnderReview,
        AuditStatus.OnHold,
        AuditStatus.Scheduled
      ];
      
      const result = await auditDataService.getAudits({
        statuses: (tableFilters.status as AuditStatusType[]) || defaultStatuses,
        sortBy: getSortByField(sortField),
        sortOrder: sortOrder,
        page: 1,
        pageSize: 100,
      });
      
      setAudits(result.items || []);
    } catch (error) {
      console.error('Failed to update priority:', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update audit priority. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleExportSelected = () => {
    const selectedAudits = audits.filter(a => selectedRowIds.has(a.id));
    
    // Create CSV content
    const headers = ['Audit Number', 'Title', 'Type', 'Status', 'Priority', 'Due Date', 'Assigned Users'];
    const rows = selectedAudits.map(audit => [
      audit.auditNumber ?? '',
      audit.title ?? audit.name ?? '',
      audit.type ?? '',
      audit.status ?? '',
      audit.priority ?? '',
      audit.scheduledEndDate ? new Date(audit.scheduledEndDate).toLocaleDateString() : '',
      audit.assignedUsers?.map(u => u.userName).join(', ') ?? ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audits-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: 'Export Complete',
      description: `Successfully exported ${selectedAudits.length} audit${selectedAudits.length !== 1 ? 's' : ''}.`,
    });
  };

  const handleDeleteAudits = () => {
    // Check if any selected audits are in progress
    const selectedAudits = audits.filter(a => selectedRowIds.has(a.id));
    const inProgressAudits = selectedAudits.filter(a => a.status === AuditStatus.InProgress);
    
    if (inProgressAudits.length > 0) {
      toast({
        title: 'Cannot Delete Active Audits',
        description: `${inProgressAudits.length} of the selected audits are currently in progress. Please cancel or complete them before deleting.`,
        variant: 'destructive',
      });
      return;
    }
    
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteSubmit = async () => {
    try {
      const selectedAudits = Array.from(selectedRowIds);
      
      // Double-check status before deleting
      const auditsToDelete = audits.filter(a => selectedRowIds.has(a.id));
      const inProgressAudits = auditsToDelete.filter(a => a.status === AuditStatus.InProgress);
      
      if (inProgressAudits.length > 0) {
        toast({
          title: 'Cannot Delete Active Audits',
          description: 'One or more selected audits are in progress and cannot be deleted.',
          variant: 'destructive',
        });
        return;
      }

      // Delete each audit
      await Promise.all(
        selectedAudits.map(auditId =>
          auditDataService.deleteAudit(auditId)
        )
      );

      toast({
        title: 'Audits Deleted',
        description: `Successfully deleted ${selectedAudits.length} audit${selectedAudits.length !== 1 ? 's' : ''}.`,
      });

      setDeleteDialogOpen(false);
      setDeleteReason('');
      setSelectedRowIds(new Set());
      
      // Reload audits
      const [sortField, sortOrder] = sortBy.split('-') as [string, 'asc' | 'desc'];
      const defaultStatuses = [
        AuditStatus.Draft,
        AuditStatus.Assigned,
        AuditStatus.InProgress,
        AuditStatus.UnderReview,
        AuditStatus.OnHold,
        AuditStatus.Scheduled
      ];
      
      const result = await auditDataService.getAudits({
        statuses: (tableFilters.status as AuditStatusType[]) || defaultStatuses,
        sortBy: getSortByField(sortField),
        sortOrder: sortOrder,
        page: 1,
        pageSize: 100,
      });
      
      setAudits(result.items || []);
    } catch (error) {
      console.error('Failed to delete audits:', error);
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete audits. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Action Card Component for better visual hierarchy
  const ActionCard = ({ icon: Icon, title, description, onClick, variant, disabled }: {
    icon: React.ElementType;
    title: string;
    description: string;
    onClick?: () => void;
    variant: 'view' | 'action' | 'delete';
    disabled?: boolean;
  }) => {
    const variantStyles = {
      view: {
        container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50'
      },
      action: {
        container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
        icon: 'text-primary dark:text-primary',
        iconBg: 'bg-primary/10 dark:bg-primary/20'
      },
      delete: {
        container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
        icon: 'text-red-600 dark:text-red-400',
        iconBg: 'bg-red-100 dark:bg-red-900/50'
      }
    };

    const styles = variantStyles[variant];

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          styles.container
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
            styles.iconBg
          )}>
            <Icon className={cn('h-6 w-6', styles.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
              <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  // Custom Actions Tab
  const CustomActionsTab = () => {
    const hasSelection = selectedRowIds.size > 0;

    return (
      <div className="p-4 space-y-3">
        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-xs text-muted-foreground">
              Select one or more audits to see available actions
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Bulk Actions
              </h4>
              {canEditAudits && (
                <ActionCard
                  icon={ListChecks}
                  title="Change Status"
                  description={`Update status for ${selectedRowIds.size} audit${selectedRowIds.size !== 1 ? 's' : ''}`}
                  variant="action"
                  onClick={handleChangeStatus}
                />
              )}
              {canEditAudits && (
                <ActionCard
                  icon={UserPlus}
                  title="Assign Users"
                  description={`Assign team members to ${selectedRowIds.size} audit${selectedRowIds.size !== 1 ? 's' : ''}`}
                  variant="action"
                  onClick={handleAssignUsers}
                />
              )}
              {canEditAudits && (
                <ActionCard
                  icon={AlertCircle}
                  title="Change Priority"
                  description={`Update priority level for selected audits`}
                  variant="action"
                  onClick={handleChangePriority}
                />
              )}
              <ActionCard
                icon={Download}
                title="Export Selected"
                description={`Download ${selectedRowIds.size} audit${selectedRowIds.size !== 1 ? 's' : ''} data`}
                variant="action"
                onClick={handleExportSelected}
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Bulk Actions
              </p>
              <div className="space-y-1">
                {canEditAudits && (
                  <Button variant="outline" className="w-full justify-start" size="sm" onClick={handleChangeStatus}>
                    <ListChecks className="mr-2 h-4 w-4" />
                    Change Status
                  </Button>
                )}
                {canEditAudits && (
                  <Button variant="outline" className="w-full justify-start" size="sm" onClick={handleAssignUsers}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Assign Users
                  </Button>
                )}
                {canEditAudits && (
                  <Button variant="outline" className="w-full justify-start" size="sm" onClick={handleChangePriority}>
                    <AlertCircle className="mr-2 h-4 w-4" />
                    Change Priority
                  </Button>
                )}
                <Button variant="outline" className="w-full justify-start" size="sm" onClick={handleExportSelected}>
                  <Download className="mr-2 h-4 w-4" />
                  Export Selected
                </Button>
                {canEditAudits && (
                  <Button variant="outline" className="w-full justify-start text-destructive" size="sm" onClick={handleDeleteAudits}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Audits
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Calculate visible columns count for badge
  const visibleColumnsCount = useMemo(() => {
    if (contextVisibleColumns && contextVisibleColumns.length > 0) {
      return contextVisibleColumns.length;
    }
    return DEFAULT_ACTIVE_AUDITS_COLUMNS.filter(col => col.visible).length;
  }, [contextVisibleColumns]);

  // Define page-specific tabs for the right panel
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <CustomActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <ActiveAuditsFiltersTab />,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <ActiveAuditsColumnsTab viewMode={viewMode} onViewModeChange={handleViewModeChange} />,
      badgeCount: visibleColumnsCount,
    },
  ];

  const getViewIcon = () => {
    switch (viewMode) {
      case 'list':
        return <LayoutList className="h-4 w-4" />;
      case 'card':
        return <LayoutGrid className="h-4 w-4" />;
      case 'kanban':
        return <Columns3 className="h-4 w-4" />;
    }
  };

  const getViewLabel = () => {
    switch (viewMode) {
      case 'list':
        return 'List View';
      case 'card':
        return 'Card View';
      case 'kanban':
        return 'Kanban View';
    }
  };

  const renderTeamsListContent = () => {
    if (teamsLoading) {
      return <p className="text-sm text-muted-foreground">Loading teams...</p>;
    }
    if (teams.length === 0) {
      return <p className="text-sm text-muted-foreground">No teams available</p>;
    }
        return (
          <div className="space-y-2">
            {teams.map((team) => {
              const IconComponent = getIconByName(team.icon);
              return (
                <button
                  key={team.id}
                  type="button"
                  className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer w-full text-left"
                  onClick={() => toggleTeam(team.id)}
                >
                  <Checkbox
                    checked={selectedTeamIds.has(team.id)}
                    onCheckedChange={() => toggleTeam(team.id)}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <div className={`p-1.5 rounded-md ${team.color ?? 'bg-primary/10'}`}>
                      {IconComponent ? <IconComponent className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{team.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {team.memberCount ?? 0} members
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        );
  };

  const renderUsersListContent = () => {
    if (usersLoading) {
      return <p className="text-sm text-muted-foreground">Loading users...</p>;
    }
    if (users.length === 0) {
      return <p className="text-sm text-muted-foreground">No users available</p>;
    }
        return (
          <div className="space-y-2">
            {users.map((user) => (
              <button
                key={user.id}
                type="button"
                className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer w-full text-left"
                onClick={() => toggleUser(user.id)}
              >
                <Checkbox
                  checked={selectedUserIds.has(user.id)}
                  onCheckedChange={() => toggleUser(user.id)}
                />
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.avatar} />
                  <AvatarFallback>
                    {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() ?? '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              </button>
            ))}
          </div>
        );
  };

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* Page Header */}
        <div className="border-b bg-card p-6 flex-shrink-0" data-tour="active-audits-header">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-6 w-6 text-primary" />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold tracking-tight">Active Audits</h1>
                  {auditsModule?.mode === 'demo' && (
                    <Badge variant="secondary" className="text-xs">
                      Demo Mode
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage and track active audits across your organisation
                </p>
              </div>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-3">
              {/* View Mode Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-tour="active-audits-view-mode">
                    {getViewIcon()}
                    <span className="ml-2">{getViewLabel()}</span>
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleViewModeChange('list')}>
                    <LayoutList className="mr-2 h-4 w-4" />
                    List View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleViewModeChange('card')}>
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    Card View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleViewModeChange('kanban')}>
                    <Columns3 className="mr-2 h-4 w-4" />
                    Kanban View
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {canCreateAudits && (
                <Button onClick={() => navigate('/audits/create')} data-tour="active-audits-new-button">
                  <Plus className="mr-2 h-4 w-4" />
                  New Audit
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Search Bar with Group By and Sort */}
        <div className="border-b bg-card px-6 py-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Search Bar - Smaller width with white background */}
            <div className="relative w-80" data-tour="active-audits-search">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search audits..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white dark:bg-slate-950"
              />
            </div>

            {/* Group By - Only show for list and card views */}
            {(viewMode === 'list' || viewMode === 'card') && (
              <Select value={groupBy} onValueChange={(value: string) => setGroupBy(value as GroupBy)}>
                <SelectTrigger className="w-[200px]" data-tour="active-audits-group-sort">
                  <div className="flex items-center gap-2">
                    <FolderTree className="h-4 w-4" />
                    <SelectValue placeholder="Group by" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dueDate">Group by Due Date</SelectItem>
                  <SelectItem value="type">Group by Type</SelectItem>
                  <SelectItem value="priority">Group by Priority</SelectItem>
                  <SelectItem value="status">Group by Status</SelectItem>
                  <SelectItem value="none">No Grouping</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Sort */}
            <Select value={sortBy} onValueChange={(value: string) => setSortBy(value as SortOption)}>
              <SelectTrigger className="w-[220px]">
                <div className="flex items-center gap-2">
                  <ArrowUpDown className="h-4 w-4" />
                  <SelectValue placeholder="Sort by" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dueDate-asc">Due Date (Earliest)</SelectItem>
                <SelectItem value="dueDate-desc">Due Date (Latest)</SelectItem>
                <SelectItem value="createdDate-asc">Created Date (Oldest)</SelectItem>
                <SelectItem value="createdDate-desc">Created Date (Newest)</SelectItem>
                <SelectItem value="priority-asc">Priority (Low-High)</SelectItem>
                <SelectItem value="priority-desc">Priority (High-Low)</SelectItem>
                <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                <SelectItem value="name-desc">Name (Z-A)</SelectItem>
              </SelectContent>
            </Select>

            <div className="text-sm text-muted-foreground ml-auto">
              {filteredAudits.length} audit{filteredAudits.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0" data-tour="active-audits-list">
          {(loading || isApplyingFilters) ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <p className="text-lg font-medium">Loading audits...</p>
              </div>
            </div>
          ) : (
            <>
              {viewMode === 'list' && (
                <AuditsListView
                  audits={filteredAudits}
                  onAuditClick={handleAuditClick}
                  selectedRowIds={selectedRowIds}
                  onSelectionChange={handleSelectionChange}
                  groupBy={groupBy}
                  visibleColumns={contextVisibleColumns && contextVisibleColumns.length > 0 
                    ? contextVisibleColumns 
                    : DEFAULT_ACTIVE_AUDITS_COLUMNS.filter(col => col.visible).map(col => col.id)}
                />
              )}
              {viewMode === 'card' && (
                <div className="flex-1 overflow-auto p-6">
                  <AuditsCardView
                    audits={filteredAudits}
                    onAuditClick={handleAuditClick}
                    groupBy={groupBy}
                    onEdit={handleEditAudit}
                    onDuplicate={handleDuplicateAudit}
                    onDelete={handleDeleteAudit}
                  />
                </div>
              )}
              {viewMode === 'kanban' && (
                <div className="flex-1 overflow-auto">
                  <AuditsKanbanView
                    audits={filteredAudits}
                    onAuditClick={handleAuditClick}
                    onEdit={handleEditAudit}
                    onDuplicate={handleDuplicateAudit}
                    onDelete={handleDeleteAudit}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} />

      {/* Product Tour */}
      <ProductTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />

      {/* Change Status Dialog */}
      <Dialog open={changeStatusDialogOpen} onOpenChange={setChangeStatusDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Change Audit Status</DialogTitle>
            <DialogDescription>
              Select a new status to apply to {selectedRowIds.size} selected audit{selectedRowIds.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={selectedStatus} onValueChange={(value: string) => setSelectedStatus(value as AuditStatusType)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AuditStatus.Draft}>Draft</SelectItem>
                  <SelectItem value={AuditStatus.Scheduled}>Scheduled</SelectItem>
                  <SelectItem value={AuditStatus.Assigned}>Assigned</SelectItem>
                  <SelectItem value={AuditStatus.InProgress}>In Progress</SelectItem>
                  <SelectItem value={AuditStatus.UnderReview}>Under Review</SelectItem>
                  <SelectItem value={AuditStatus.OnHold}>On Hold</SelectItem>
                  <SelectItem value={AuditStatus.Completed}>Completed</SelectItem>
                  <SelectItem value={AuditStatus.Cancelled}>Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangeStatusSubmit}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Users Dialog */}
      <Dialog open={assignUsersDialogOpen} onOpenChange={setAssignUsersDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Assign Users to Audits</DialogTitle>
            <DialogDescription>
              Assign users or teams to {selectedRowIds.size} selected audit{selectedRowIds.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Selection Mode Toggle */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {selectionMode === 'teams' ? 'Select by Team' : 'Select Individual Users'}
                </span>
              </div>
              <Switch
                checked={selectionMode === 'users'}
                onCheckedChange={(checked) => handleModeChange(checked ? 'users' : 'teams')}
              />
            </div>

            {/* Team Selection */}
            {selectionMode === 'teams' && (
              <div className="space-y-2">
                <Label>Select Teams</Label>
                <ScrollArea className="h-[300px] border rounded-md p-4">
                  {renderTeamsListContent()}
                </ScrollArea>
              </div>
            )}

            {/* User Selection */}
            {selectionMode === 'users' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Select Users</Label>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAllUsers}>
                      Select All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={clearAllUsers}>
                      Clear All
                    </Button>
                  </div>
                </div>
                <ScrollArea className="h-[300px] border rounded-md p-4">
                  {renderUsersListContent()}
                </ScrollArea>
              </div>
            )}

            {/* Selection Summary */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{selectedUserIds.size}</span> user{selectedUserIds.size !== 1 ? 's' : ''} selected
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignUsersDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignUsersSubmit} disabled={selectedUserIds.size === 0}>
              Assign Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Priority Dialog */}
      <Dialog open={changePriorityDialogOpen} onOpenChange={setChangePriorityDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Change Audit Priority</DialogTitle>
            <DialogDescription>
              Select a new priority to apply to {selectedRowIds.size} selected audit{selectedRowIds.size !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Priority</Label>
              <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePriorityDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangePrioritySubmit}>
              Update Priority
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Audits</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedRowIds.size} selected audit{selectedRowIds.size !== 1 ? 's' : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for Deletion (Optional)</Label>
              <Textarea
                value={deleteReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDeleteReason(e.target.value)}
                placeholder="Enter a reason for deleting these audits..."
                rows={3}
              />
            </div>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-destructive font-medium">
                    Warning: This will permanently delete the selected audits and all associated data.
                  </p>
                  <p className="text-xs text-destructive/80 mt-1">
                    Note: Active audits (in progress) cannot be deleted. Please cancel or complete them first.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSubmit}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Audits
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageWithRightPanel>
  );
}
